import type { ReactNode } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { InvoiceFact, InvoiceModel } from "../lib/invoice-model";
import "../tax-invoice.css";

/**
 * The tax invoice as printed from the back office (`pages/invoicing/Print.tsx`), drawn from
 * `InvoiceModel` (`lib/invoice-model.ts`). Nothing here computes; every value is a string the model
 * formatted from Billing's own field.
 *
 * A copy of `steelyard-portal/components/tax-invoice.tsx` (2026-10-05, owner decision: the admin
 * prints the same document the customer prints and downloads), kept in step by hand — the two apps
 * share no code. Its `sy-tinv-*` rules are `src/tax-invoice.css`, a plain-CSS copy of the portal's
 * `app/_portal-invoice.scss`. Differences: Tailwind's `sr-only`/`text-right`/`whitespace-nowrap`
 * stand in for Bootstrap's helpers, and the D-40 banner keeps the back office's own DRAFT wording,
 * printed on purpose so a draft on paper never passes for the final tax invoice.
 *
 * The article keeps `data-slot="invoice-document"` and the controls `data-print="hide"`, the two
 * hooks `src/print.css` targets for A4 geometry and hiding controls. `theme/domain/invoice-document.tsx`
 * — the on-screen detail view, held to the frozen baseline by `domain/parity.test.tsx` — is unchanged.
 */
const QR_MAX_LENGTH = 2900;

const FACTS_LAYOUT = { grid: "sy-tinv-facts", strip: "sy-tinv-meta" } as const;

function Facts({ facts, layout = "grid" }: { facts: readonly InvoiceFact[]; layout?: keyof typeof FACTS_LAYOUT }) {
  return (
    <dl className={FACTS_LAYOUT[layout]}>
      {facts.map((fact) => (
        <div key={fact.label}>
          <dt>{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Infra Junction's mark — the paths in `theme/components/logo.tsx` — in `currentColor`, which
 * `.sy-tinv-mark` sets to the brand's blue. Not `Logo` itself: that one draws the mark on a white tile
 * for the dark sidebar, which the white sheet does not need.
 */
function Mark() {
  return (
    <svg className="sy-tinv-mark" viewBox="-4 -6 318 362" fill="currentColor" aria-hidden>
      <path d="M291,263 L291,93 L154,18 L18,93 L18,268 L106,319" fill="none" stroke="currentColor" strokeWidth={36} />
      <path d="M78,139 L127,122 L127,171 L78,177 Z M85,187 L127,187 L127,352 L85,328 Z M157,133 L222,120 L222,298 C222,330 202,350 172,350 L157,350 L157,312 L166,312 C177,312 184,305 184,294 L184,151 L157,155 Z" />
    </svg>
  );
}

export function TaxInvoice({ model, children }: { model: InvoiceModel; children?: ReactNode }) {
  const meta: InvoiceFact[] = [
    { label: "Invoice no.", value: model.invoiceNumber },
    { label: "Invoice date", value: model.invoiceDate },
    { label: "Financial year", value: model.financialYear },
    ...(model.orderNumber ? [{ label: "Order no.", value: model.orderNumber }] : []),
    { label: "Place of supply", value: model.recipient.placeOfSupply },
  ];
  const showQr = model.irnQr !== null && model.irnQr.length <= QR_MAX_LENGTH;

  return (
    <>
      {children ? (
        <div data-print="hide" className="sy-tinv-toolbar">
          {children}
        </div>
      ) : null}

      <article data-slot="invoice-document" aria-label={`Tax invoice ${model.invoiceNumber}`} className="sy-tinv">
        {model.cancelled ? (
          <span className="sy-tinv-watermark" aria-hidden>
            Cancelled
          </span>
        ) : null}

        <header className="sy-tinv-head">
          <div className="sy-tinv-brand">
            <Mark />
            <div>
              <p className="sy-tinv-brand-name">{model.supplier.name}</p>
              <p className="sy-tinv-brand-sub">Metal hardware for trade &amp; retail</p>
            </div>
          </div>
          <div className="sy-tinv-title">
            <h1>Tax invoice</h1>
            <p>Under section 31 of the CGST Act, 2017</p>
            <span className={model.cancelled ? "sy-tinv-pill sy-tinv-pill-danger" : "sy-tinv-pill"}>
              {model.cancelled ? "Cancelled" : `IRN · ${model.irnStatus}`}
            </span>
          </div>
        </header>

        {model.reviewPending ? (
          <p className="sy-tinv-banner" role="status">
            DRAFT — awaiting Super Admin review. This is not a final tax invoice: its discount and round off may still change.
          </p>
        ) : null}

        <Facts facts={meta} layout="strip" />

        <div className="sy-tinv-parties">
          <section aria-labelledby="tinv-supplier">
            <h2 id="tinv-supplier" className="sy-tinv-label">
              Supplier
            </h2>
            <p className="sy-tinv-party-name">{model.supplier.name}</p>
            {model.supplier.address ? <p className="sy-tinv-party-address">{model.supplier.address}</p> : null}
            <dl className="sy-tinv-party-facts">
              <div>
                <dt>GSTIN</dt>
                <dd className={model.supplier.gstinMissing ? "sy-tinv-missing" : "sy-tinv-code"}>{model.supplier.gstin}</dd>
              </div>
              <div>
                <dt>State</dt>
                <dd>{model.supplier.stateCode}</dd>
              </div>
            </dl>
          </section>
          <section aria-labelledby="tinv-recipient">
            <h2 id="tinv-recipient" className="sy-tinv-label">
              Billed to
            </h2>
            <p className="sy-tinv-party-name">{model.recipient.name}</p>
            <p className="sy-tinv-party-address">{model.recipient.address}</p>
            <dl className="sy-tinv-party-facts">
              <div>
                <dt>GSTIN</dt>
                <dd className={model.recipient.gstinMissing ? "sy-tinv-missing" : "sy-tinv-code"}>{model.recipient.gstin}</dd>
              </div>
              <div>
                <dt>Place of supply</dt>
                <dd>{model.recipient.placeOfSupply}</dd>
              </div>
            </dl>
          </section>
        </div>

        <div className="sy-tinv-supply">
          <strong>{model.taxHead.label}</strong>
          <span>
            Quantity basis: <strong>{model.basis.label}</strong>
          </span>
        </div>

        <h2 className="sy-tinv-section">Items</h2>
        <div className="sy-tinv-scroll">
          <table className="sy-tinv-table">
            <caption className="sr-only">Invoice lines with HSN, quantity, rate, taxable value, GST rate and amount</caption>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Item &amp; description</th>
                <th scope="col">HSN</th>
                <th scope="col" className="text-right">
                  Qty
                </th>
                <th scope="col" className="text-right">
                  Rate
                </th>
                <th scope="col" className="text-right">
                  Taxable value
                </th>
                <th scope="col" className="text-right">
                  GST
                </th>
                <th scope="col" className="text-right">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {model.lines.map((line) => (
                <tr key={line.no}>
                  <td className="sy-tinv-no">{line.no}</td>
                  <td>
                    <span className="sy-tinv-item">{line.name}</span>
                    {line.detail ? <span className="sy-tinv-item-detail">{line.detail}</span> : null}
                    {line.discounted ? (
                      <span className="sy-tinv-item-discount">
                        Gross {line.gross} · trade discount {line.tradeDiscount}
                      </span>
                    ) : null}
                  </td>
                  <td className="sy-tinv-code">{line.hsn}</td>
                  <td className="whitespace-nowrap text-right">
                    {line.quantity} {line.unit}
                  </td>
                  <td className="whitespace-nowrap text-right">{line.rate}</td>
                  <td className="whitespace-nowrap text-right">{line.taxable}</td>
                  <td className="text-right">{line.gstRate}</td>
                  <td className="whitespace-nowrap text-right sy-tinv-amount">{line.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="sy-tinv-section">Tax breakdown</h2>
        <div className="sy-tinv-scroll">
          <table className="sy-tinv-table">
            <caption className="sr-only">GST on each line, as Billing calculated it</caption>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">HSN</th>
                <th scope="col" className="text-right">
                  Taxable value
                </th>
                <th scope="col" className="text-right">
                  GST rate
                </th>
                {model.taxHead.showCgstSgst ? (
                  <>
                    <th scope="col" className="text-right">
                      CGST
                    </th>
                    <th scope="col" className="text-right">
                      SGST
                    </th>
                  </>
                ) : null}
                {model.taxHead.showIgst ? (
                  <th scope="col" className="text-right">
                    IGST
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {model.lines.map((line) => (
                <tr key={line.no}>
                  <td className="sy-tinv-no">{line.no}</td>
                  <td className="sy-tinv-code">{line.hsn}</td>
                  <td className="whitespace-nowrap text-right">{line.taxable}</td>
                  <td className="text-right">{line.gstRate}</td>
                  {model.taxHead.showCgstSgst ? (
                    <>
                      <td className="whitespace-nowrap text-right">{line.cgst}</td>
                      <td className="whitespace-nowrap text-right">{line.sgst}</td>
                    </>
                  ) : null}
                  {model.taxHead.showIgst ? <td className="whitespace-nowrap text-right">{line.igst}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="sy-tinv-bottom">
          <div>
            {model.amountInWords ? (
              <div className="sy-tinv-words">
                <h2 className="sy-tinv-label">Amount in words</h2>
                <p>{model.amountInWords}</p>
              </div>
            ) : null}
            {model.payment.length > 0 ? (
              <section aria-labelledby="tinv-payment">
                <h2 id="tinv-payment" className="sy-tinv-section">
                  Order &amp; payment
                </h2>
                <Facts facts={model.payment} />
              </section>
            ) : null}
          </div>

          <section className="sy-tinv-totals" aria-labelledby="tinv-totals">
            <h2 id="tinv-totals" className="sr-only">
              Totals
            </h2>
            <dl>
              {model.totals.map((total) => (
                <div key={total.label}>
                  <dt>{total.label}</dt>
                  <dd>{total.value}</dd>
                </div>
              ))}
            </dl>
            <p className="sy-tinv-grand">
              <span>Grand total</span>
              <strong data-slot="invoice-grand-total">{model.grandTotal}</strong>
            </p>
          </section>
        </div>

        <section className="sy-tinv-compliance-wrap" aria-labelledby="tinv-compliance">
          <h2 id="tinv-compliance" className="sy-tinv-section">
            Compliance
          </h2>
          <div className="sy-tinv-compliance">
            {showQr ? (
              <QRCodeSVG value={model.irnQr!} size={112} role="img" aria-label="IRN QR code" className="sy-tinv-qr" />
            ) : null}
            <div>
              <Facts facts={model.compliance} />
              {model.irn ? (
                <dl className="sy-tinv-facts">
                  <div className="sy-tinv-wide">
                    <dt>IRN</dt>
                    <dd className="sy-tinv-code sy-tinv-break">{model.irn}</dd>
                  </div>
                </dl>
              ) : null}
              {model.irnQr && !showQr ? (
                <dl className="sy-tinv-facts">
                  <div className="sy-tinv-wide">
                    <dt>Signed QR payload</dt>
                    <dd className="sy-tinv-code sy-tinv-break">{model.irnQr}</dd>
                  </div>
                </dl>
              ) : null}
              {showQr ? <p className="sy-tinv-note">Scan the QR to verify this e-invoice on the government portal.</p> : null}
            </div>
          </div>
          {model.basis.rule ? <p className="sy-tinv-note">{model.basis.rule}</p> : null}
        </section>

        <footer className="sy-tinv-foot">
          <span>
            {model.supplier.name}
            {model.supplier.gstinMissing ? "" : ` · GSTIN ${model.supplier.gstin}`}
          </span>
          <span>This is a computer-generated invoice.</span>
        </footer>
      </article>
    </>
  );
}
